import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { setApiModeResolver } from '@/services/api';
import { authService, AuthResponse, LoginCredentials, RegisterData } from '@/services/auth.service';
import { handleApiError, apiClient } from '@/services/api';
import { trackUserRegistered } from '@/services/analytics';
import { normalizeProfile } from '@/services/profile.service';
import { usePipelineStore } from '@/store/pipeline.store';
import { useProfileStore, type Profile } from '@/store/profile.store';

/**
 * Check if local profile has meaningful data (not just a blank stub).
 */
function isRealProfile(p: Profile | null): boolean {
  if (!p) return false;
  return !!(
    (p.firstName && p.lastName) ||
    (p.experience && p.experience.length > 0) ||
    (p.skills && p.skills.length > 0)
  );
}

/**
 * Extract access/refresh tokens from an auth response, supporting both the
 * nested `tokens` shape and the flat backward-compatible shape.
 * Throws explicitly if the backend response is missing tokens, instead of
 * silently passing `undefined` through as a `string`.
 */
function extractTokens(response: AuthResponse): { accessToken: string; refreshToken: string } {
  const accessToken = response.tokens?.accessToken ?? response.accessToken;
  const refreshToken = response.tokens?.refreshToken ?? response.refreshToken;

  if (!accessToken || !refreshToken) {
    throw new Error('Authentication response is missing tokens');
  }

  return { accessToken, refreshToken };
}

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    hasHydrated,
    mode,
    login,
    logout,
    setUser,
    setLoading,
    setMode,
  } = useAuthStore();

  // Keep api client aware of current mode to avoid redirects in local mode
  useEffect(() => {
    setApiModeResolver(() => mode);
  }, [mode]);

  /**
   * Login with email and password
   */
  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      try {
        const response = await authService.login(credentials);

        const userWithDate = {
          ...response.user,
          createdAt: new Date(response.user.createdAt),
        };

        login(userWithDate, extractTokens(response));

        // Check if profile exists and detect conflicts
        try {
          const profileCheck = await apiClient.get('/api/v1/profile/exists');
          const hasServerProfile = profileCheck.data?.data?.exists;
          const localProfile = useProfileStore.getState().profile;

          if (hasServerProfile) {
            const serverResponse = await apiClient.get('/api/v1/profile');
            const serverProfile = normalizeProfile(serverResponse.data.data as any);

            if (isRealProfile(localProfile) && localProfile) {
              // Conflict: both profiles have data
              useProfileStore.getState().setConflict({
                localProfile,
                serverProfile,
              });
              navigate('/app/pipeline');
            } else {
              // No conflict: just use server profile
              useProfileStore.getState().setProfile(serverProfile);
              useProfileStore.getState().setLocalMode(false);
              usePipelineStore.setState({ hasFetchedFromBackend: false });
              navigate('/app/pipeline');
            }
          } else {
            navigate('/app/onboarding');
          }
        } catch (err) {
          console.error('[useAuth] Profile check failed after login:', err);
          navigate('/app/onboarding');
        }

        return response;
      } catch (error) {
        const message = handleApiError(error);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [login, navigate, setLoading]
  );

  /**
   * Register new user
   */
  const handleRegister = useCallback(
    async (data: RegisterData) => {
      setLoading(true);
      try {
        const response = await authService.register(data);

        const userWithDate = {
          ...response.user,
          createdAt: new Date(response.user.createdAt),
        };

        login(userWithDate, extractTokens(response));

        // Track registration — fire-and-forget, no PII
        trackUserRegistered();

        // New users always go to onboarding
        navigate('/app/onboarding');

        return response;
      } catch (error) {
        const message = handleApiError(error);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [login, navigate, setLoading]
  );

  /**
   * Logout current user
   */
  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      // Reset pipeline so next login fetches fresh data
      usePipelineStore.setState({ hasFetchedFromBackend: false, jobs: [] });
      navigate('/login');
    }
  }, [logout, navigate]);

  /**
   * Check authentication status and load current user
   */
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setUser(null);
      return false;
    }

    setLoading(true);
    try {
      const currentUser = await authService.getCurrentUser();
      const userWithDate = {
        ...currentUser,
        createdAt: new Date(currentUser.createdAt),
      };
      setUser(userWithDate);
      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  /**
   * Login with Google OAuth
   */
  const handleGoogleLogin = useCallback(
    async (code: string) => {
      setLoading(true);
      try {
        const response = await authService.loginWithGoogle({
          code,
          redirectUri: `${window.location.origin}/auth/callback`,
        });

        const userWithDate = {
          ...response.user,
          createdAt: new Date(response.user.createdAt),
        };

        login(userWithDate, extractTokens(response));

        // Check if profile exists and detect conflicts
        try {
          const profileCheck = await apiClient.get('/api/v1/profile/exists');
          const hasServerProfile = profileCheck.data?.data?.exists;
          const localProfile = useProfileStore.getState().profile;

          if (hasServerProfile) {
            const serverResponse = await apiClient.get('/api/v1/profile');
            const serverProfile = normalizeProfile(serverResponse.data.data as any);

            if (isRealProfile(localProfile) && localProfile) {
              // Conflict: both profiles have data
              useProfileStore.getState().setConflict({
                localProfile,
                serverProfile,
              });
              navigate('/app/pipeline');
            } else {
              useProfileStore.getState().setProfile(serverProfile);
              useProfileStore.getState().setLocalMode(false);
              usePipelineStore.setState({ hasFetchedFromBackend: false });
              navigate('/app/pipeline');
            }
          } else {
            navigate('/app/onboarding');
          }
        } catch (err) {
          console.error('[useAuth] Profile check failed after Google login:', err);
          navigate('/app/onboarding');
        }

        return response;
      } catch (error) {
        const message = handleApiError(error);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [login, navigate, setLoading]
  );

  /**
   * Login with LinkedIn OAuth
   */
  const handleLinkedInLogin = useCallback(
    async (code: string) => {
      setLoading(true);
      try {
        const response = await authService.loginWithLinkedIn({
          code,
          redirectUri: `${window.location.origin}/auth/callback`,
        });

        const userWithDate = {
          ...response.user,
          createdAt: new Date(response.user.createdAt),
        };

        login(userWithDate, extractTokens(response));

        if (!response.user.hasProfile) {
          navigate('/app/onboarding');
        } else {
          navigate('/app/pipeline');
        }

        return response;
      } catch (error) {
        const message = handleApiError(error);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [login, navigate, setLoading]
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    hasHydrated,
    mode,
    setMode,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    checkAuth,
    loginWithGoogle: handleGoogleLogin,
    loginWithLinkedIn: handleLinkedInLogin,
  };
}
